import { useEffect } from 'react';

/**
 * ref 요소를 포함하는 스크롤 가능한 조상(overflow: auto/scroll)을 찾습니다.
 * Layout의 main 등 실제 페이지 스크롤 컨테이너를 찾기 위함.
 */
function getScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let p = el.parentElement;
  while (p && p !== document.body) {
    const style = getComputedStyle(p);
    const oy = style.overflowY;
    const o = style.overflow;
    if (
      (oy === 'auto' || oy === 'scroll' || o === 'auto' || o === 'scroll') &&
      p.scrollHeight > p.clientHeight
    ) {
      return p;
    }
    p = p.parentElement;
  }
  return null;
}

/**
 * Enables scroll handoff from a nested scrollable (e.g., table) to the page.
 * When the nested scroller reaches the top/bottom and the user keeps scrolling
 * in the same direction, this will forward the scroll to the page for natural chaining.
 * 실제 스크롤 컨테이너(Layout main 등)를 찾아 그쪽을 스크롤합니다.
 *
 * 모바일: 터치 핸드오프는 사용하지 않음. preventDefault + scrollTop 보정 시 관성 스크롤이
 * 사라져 무빙이 딱딱해지므로, 터치 스크롤은 전부 네이티브에 맡겨 부드럽게 유지합니다.
 * 데스크톱: 휠만 핸드오프 적용.
 */
export function useScrollHandoff(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const getScrollInfo = () => {
      const scrollTop = el.scrollTop;
      const maxScroll = el.scrollHeight - el.clientHeight;
      return { scrollTop, maxScroll };
    };

    const scrollPageBy = (deltaY: number) => {
      const pageScroller = getScrollableAncestor(el);
      const scroller = pageScroller || (document.scrollingElement as HTMLElement) || document.documentElement;
      if (scroller && 'scrollTop' in scroller) {
        (scroller as HTMLElement).scrollTop += deltaY;
      }
    };

    const onWheel = (e: WheelEvent) => {
      // Only vertical intent
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const { scrollTop, maxScroll } = getScrollInfo();
      const atTop = scrollTop <= 0;
      const atBottom = scrollTop >= maxScroll - 1; // tolerate 1px

      if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
        // Forward to page and prevent inner overscroll rubber-banding
        e.preventDefault();
        scrollPageBy(e.deltaY);
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      el.removeEventListener('wheel', onWheel as any);
    };
  }, [ref]);
}


